<template>
  <div
    class="base-input"
    :class="[wrapperClass, variantClass]"
  >
    <label
      v-if="label"
      :for="id"
    >{{ label }}</label>
    <div
      class="input-wrapper"
      :class="[inputWrapperClass, { 'has-append': hasAppend }]"
    >
      <input
        v-if="hasMask"
        v-mask="mask"
        v-bind="inputAttrs"
        @input="$emit('update:modelValue', $event.target.value)"
      >
      <input
        v-else
        v-bind="inputAttrs"
        @input="$emit('update:modelValue', $event.target.value)"
      >
      <div
        v-if="hasAppend"
        class="input-append"
      >
        <slot name="append"></slot>
      </div>
    </div>
    <div
      v-if="error"
      class="invalid-feedback"
    >
      {{ error }}
    </div>
  </div>
</template>

<script>
  export default {
    name: 'BaseInput',
    inheritAttrs: false,
    props: {
      modelValue: {
        type: [String, Number],
        default: ''
      },
      id: {
        type: String,
        default: null
      },
      label: {
        type: String,
        default: null
      },
      type: {
        type: String,
        default: 'text'
      },
      error: {
        type: [String, Boolean],
        default: null
      },
      mask: {
        type: [String, Array],
        default: null
      },
      inputClass: {
        type: [String, Array, Object],
        default: null
      },
      wrapperClass: {
        type: [String, Array, Object],
        default: null
      },
      inputWrapperClass: {
        type: [String, Array, Object],
        default: null
      },
      variant: {
        type: String,
        default: 'primary'
      }
    },
    emits: ['update:modelValue'],
    computed: {
      hasAppend() {
        return Boolean(this.$slots.append)
      },
      inputAttrs() {
        return {
          id: this.id,
          type: this.type,
          class: ['form-control', this.inputClass, { 'is-invalid': Boolean(this.error) }],
          value: this.modelValue,
          ...this.$attrs
        }
      },
      hasMask() {
        if (Array.isArray(this.mask)) {
          return this.mask.length > 0
        }

        return typeof this.mask === 'string' && this.mask.trim().length > 0
      },
      variantClass() {
        return this.variant ? `variant-${this.variant}` : null
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main.scss' as main;

  .base-input {
    --base-input-focus: #f58a48; // Energy orange
    --base-input-focus-shadow: rgba(245, 138, 72, 0.1);
  }

  .base-input.variant-success {
    --base-input-focus: #2e7d32;
    --base-input-focus-shadow: rgba(46, 125, 50, 0.1);
  }

  label {
    display: block;
    font-size: var(--fs-xs);
    font-weight: var(--fw-medium);
    color: main.$color-energy-dark-gray;
    margin-bottom: 6px;
    letter-spacing: var(--ls-tight);
  }

  .input-wrapper {
    position: relative;
  }

  .input-wrapper.has-append .form-control {
    padding-right: 2.5rem;
  }

  .form-control {
    width: 100%;
    padding: 12px 14px;
    font-size: var(--fs-base);
    letter-spacing: var(--ls-tight);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 10px; // More rounded like iOS
    transition: all 0.2s ease;
    -webkit-appearance: none;
    appearance: none;
    background: white;

    &:focus {
      outline: none;
      border-color: var(--base-input-focus);
      box-shadow: 0 0 0 3px var(--base-input-focus-shadow);
      -webkit-box-shadow: 0 0 0 3px var(--base-input-focus-shadow);
    }

    &.is-invalid {
      border-color: #dc3545;

      &:focus {
        box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
        -webkit-box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
      }
    }

    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
      font-weight: 400;
    }

    &.text-uppercase {
      text-transform: uppercase;
    }
  }

  .invalid-feedback {
    display: block;
    margin-top: 6px;
    font-size: var(--fs-xs);
    color: #dc3545;
  }

  .input-append {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
</style>
